"use client";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useCallback, useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { Passkey } from "better-auth/plugins/passkey";
import { Skeleton } from "@/components/ui/skeleton";
import { Trash2 } from "lucide-react";
import { authClient } from "@/lib/auth-client";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

export function PasskeySettings() {
  const { t } = useTranslation();
  const [passkeys, setPasskeys] = useState<Passkey[]>([]);
  const [isLoadingPasskeys, setIsLoadingPasskeys] = useState(true);
  const [isAddingPasskey, setIsAddingPasskey] = useState(false);
  const [isRemovingPasskey, setIsRemovingPasskey] = useState<string | null>(
    null,
  );
  const [passkeyToRemove, setPasskeyToRemove] = useState<Passkey | null>(null);
  const [newPasskeyName, setNewPasskeyName] = useState("");

  const refreshPasskeys = useCallback(async () => {
    setIsLoadingPasskeys(true);
    try {
      const { data, error } = await authClient.passkey.listUserPasskeys();
      if (error) {
        throw error;
      }
      setPasskeys(data || []);
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to load passkeys.";
      console.error("Failed to fetch passkeys:", error);
      toast.error(errorMessage);
      setPasskeys([]);
    } finally {
      setIsLoadingPasskeys(false);
    }
  }, []);

  useEffect(() => {
    refreshPasskeys();
  }, [refreshPasskeys]);

  const handleAddPasskey = async () => {
    setIsAddingPasskey(true);
    try {
      await authClient.passkey.addPasskey({
        name: newPasskeyName || undefined,
      });
      toast.success(t("passkeySettings.addSuccess"));
      setNewPasskeyName("");
      await refreshPasskeys();
    } catch (error: unknown) {
      console.error("Failed to add passkey:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : t("passkeySettings.addErrorGeneric");
      if (
        errorMessage.includes("cancelled") ||
        errorMessage.includes("canceled")
      ) {
        toast.info(t("passkeySettings.addCancelled"));
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setIsAddingPasskey(false);
    }
  };

  const handleRemovePasskeyConfirm = async () => {
    if (!passkeyToRemove) return;

    setIsRemovingPasskey(passkeyToRemove.credentialID);
    try {
      const { error } = await authClient.passkey.deletePasskey({
        id: passkeyToRemove.credentialID,
      });
      if (error) {
        throw error;
      }
      toast.success(t("passkeySettings.removeSuccess"));
      await refreshPasskeys();
    } catch (error: unknown) {
      console.error("Failed to remove passkey:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : t("passkeySettings.removeErrorGeneric");
      toast.error(errorMessage);
    } finally {
      setIsRemovingPasskey(null);
      setPasskeyToRemove(null);
    }
  };

  return (
    <>
      <div className="space-y-4 p-1">
        {isLoadingPasskeys ? (
          <div className="space-y-2">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        ) : passkeys.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {t("passkeySettings.noPasskeys")}
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("passkeySettings.identifierHeader")}</TableHead>
                <TableHead>{t("passkeySettings.createdAtHeader")}</TableHead>
                <TableHead className="text-right">
                  {t("common.actions")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {passkeys.map((key) => (
                <TableRow key={key.credentialID}>
                  <TableCell className="font-medium">
                    {key.name || key.credentialID.substring(0, 12)}...
                  </TableCell>
                  <TableCell>
                    {new Date(key.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setPasskeyToRemove(key)}
                      disabled={isRemovingPasskey === key.credentialID}
                      aria-label={`Remove passkey ${key.name || key.credentialID}`}
                    >
                      {isRemovingPasskey === key.credentialID ? (
                        <span className="loading loading-spinner loading-xs" />
                      ) : (
                        <Trash2 className="h-4 w-4 text-destructive" />
                      )}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {/* Removed mt-6, rely on parent space-y-4 */}
        <div className="flex items-end gap-4">
          <div className="flex-grow space-y-2">
            <Label htmlFor="passkey-name">
              {t("passkeySettings.newNameLabel")}
            </Label>
            <Input
              id="passkey-name"
              value={newPasskeyName}
              onChange={(e) => setNewPasskeyName(e.target.value)}
              placeholder={t("passkeySettings.newNamePlaceholder")}
              disabled={isAddingPasskey || isLoadingPasskeys}
            />
          </div>
          <Button
            onClick={handleAddPasskey}
            disabled={isAddingPasskey || isLoadingPasskeys}
            className="shrink-0"
          >
            {isAddingPasskey
              ? t("common.adding")
              : t("passkeySettings.addButton")}
          </Button>
        </div>
      </div>

      <AlertDialog
        open={!!passkeyToRemove}
        onOpenChange={(open) => {
          if (!open) setPasskeyToRemove(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("common.confirmationTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("passkeySettings.removeConfirmDescription1")}{" "}
              <span className="font-medium">
                {passkeyToRemove?.name ||
                  passkeyToRemove?.credentialID.substring(0, 12)}
                ...
              </span>{" "}
              {t("passkeySettings.removeConfirmDescription2")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={!!isRemovingPasskey}>
              {t("common.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemovePasskeyConfirm}
              disabled={!!isRemovingPasskey}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isRemovingPasskey
                ? t("common.removing")
                : t("passkeySettings.removeConfirmButton")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
